import React, { useEffect, useState } from 'react';

import Box from '@material-ui/core/Box';
import Popover from '@material-ui/core/Popover';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { keepFieldFocusOnMouseDown } from '../ComposePanel/compose-panel.insert';
import { getMessages } from './variable-panel.messages';

interface IVariableFallbackPopoverProps {
  // Anchor of the clicked variable chip; null closes the popover.
  anchorEl: HTMLElement | null;
  variableKey?: string;
  onConfirm: (fallback: string) => void;
  onClose: () => void;
}

// Prompt for a requiresFallback variable's "|default" part. An empty confirm is
// a deliberate blank — the token inserts as {key|}.
export const VariableFallbackPopover: React.FC<IVariableFallbackPopoverProps> = ({
  anchorEl,
  variableKey,
  onConfirm,
  onClose
}) => {
  const termLookup = useCompanySportTerminology();
  const messages = getMessages(termLookup);
  const [draft, setDraft] = useState('');

  // A fresh prompt (new chip click) always starts empty — never a leftover draft.
  useEffect(() => {
    if (anchorEl) setDraft('');
  }, [anchorEl, variableKey]);

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box p={1.5} maxWidth={260} display="flex" flexDirection="column" gridGap={8}>
        <TextField
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          label={<IntlMsg {...messages.fallbackLabel} />}
          inputProps={{ 'aria-label': 'Default if blank' }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onConfirm(draft);
            }
          }}
          autoFocus
          fullWidth
        />
        <Typography variant="caption" color="textSecondary">
          <IntlMsg {...messages.fallbackHint} />
        </Typography>
        <PrimaryButton
          size="small"
          onMouseDown={keepFieldFocusOnMouseDown}
          onClick={() => onConfirm(draft)}
        >
          <IntlMsg {...messages.fallbackInsert} />
        </PrimaryButton>
      </Box>
    </Popover>
  );
};
